import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import { WalteroCard, WalteroTypography } from './waltero';
import * as echarts from 'echarts';

/**
 * DevicePerformanceTable - Comprehensive device performance metrics
 * 
 * Creates a table with dates as columns and performance metrics as rows:
 * - Number of transmissions
 * - Number of payloads
 * - Average RSSI
 * - Battery voltage
 * - Average temperature
 * - Boot reason
 * 
 * Also includes an hourly connectivity histogram below the table.
 */
const DevicePerformanceTable = ({ devicePerformanceData, startDate, endDate }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [bootReasonHoverData, setBootReasonHoverData] = useState(null);
  const [bootReasonHoverPosition, setBootReasonHoverPosition] = useState({ x: 0, y: 0 });

  const updateHourlyChart = useCallback(() => {
    if (!chartInstanceRef.current) return;
    
    const hourlyPattern = devicePerformanceData.device.connectivityPattern.hourlyPattern;
    
    // Process hourly data (similar to HourlyConnectivityHistogram)
    const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      transmissions: 0,
      label: `${hour.toString().padStart(2, '0')}:00`
    }));

    hourlyPattern.forEach(hourData => {
      if (hourData.hour >= 0 && hourData.hour <= 23) {
        hourlyData[hourData.hour].transmissions = hourData.transmissionCount || 0;
      }
    });

    const option = {
      title: {
        text: 'Hourly Connection Pattern',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#2c3e50'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          const data = params[0];
          const hourData = hourlyData[data.dataIndex];
          return `
            <strong>${hourData.label}</strong><br/>
            Transmissions: <strong>${hourData.transmissions}</strong>
          `;
        }
      },
      xAxis: {
        type: 'category',
        data: hourlyData.map(h => h.label),
        axisLabel: {
          interval: 2,
          fontSize: 10
        },
        name: 'Hour of Day',
        nameLocation: 'center',
        nameGap: 30
      },
      yAxis: {
        type: 'value',
        name: 'Transmissions',
        nameLocation: 'center',
        nameGap: 40
      },
      series: [{
        name: 'Transmissions',
        type: 'bar',
        data: hourlyData.map(h => h.transmissions),
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#4fc3f7' },
            { offset: 1, color: '#1976d2' }
          ])
        },
        emphasis: {
          itemStyle: {
            color: '#0d47a1'
          }
        }
      }],
      grid: {
        left: '60px',
        right: '30px',
        top: '80px',
        bottom: '60px'
      }
    };

    chartInstanceRef.current.setOption(option);
  }, [devicePerformanceData]);

  useEffect(() => {
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }
    
    if (chartInstanceRef.current && devicePerformanceData?.device?.connectivityPattern?.hourlyPattern) {
      updateHourlyChart();
    }
    
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [devicePerformanceData, updateHourlyChart]);

  // Create date range for table columns
  const createDateRange = () => {
    if (!startDate || !endDate) return [];
    
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      dates.push(new Date(date).toISOString().split('T')[0]);
    }
    
    return dates;
  };

  // Create data lookup maps for each metric by date
  const createDataMaps = (dateRange) => {
    const maps = {
      transmissions: {},
      payloads: {},
      rssi: {},
      battery: {},
      temperature: {},
      bootReason: {}
    };

    if (!devicePerformanceData?.device) return maps;

    // Process connectivity stats (transmissions)
    devicePerformanceData.device.connectivityStats?.forEach(day => {
      maps.transmissions[day.date] = day.transmissionCount || 0;
    });

    // Process daily battery data - handle different possible data structures
    if (devicePerformanceData.device.batteryStats?.dailyBattery) {
      devicePerformanceData.device.batteryStats.dailyBattery.forEach(day => {
        maps.battery[day.date] = (day.averageVoltage || day.voltage || 0).toFixed(2);
      });
    }

    // Process daily temperature data
    if (devicePerformanceData.device.temperatureStats?.dailyTemperature) {
      devicePerformanceData.device.temperatureStats.dailyTemperature.forEach(day => {
        maps.temperature[day.date] = day.averageTemperature || day.temperature || 0;
      });
    }

    // Process boot reason data from bootReasonMatrix
    if (devicePerformanceData.device.bootReasonStats?.bootReasonMatrix) {
      const bootReasonMatrix = devicePerformanceData.device.bootReasonStats.bootReasonMatrix;
      const summary = devicePerformanceData.device.bootReasonStats.summary || {};
      
      // Group boot reasons by date
      const bootReasonsByDate = {};
      bootReasonMatrix.forEach(entry => {
        const date = entry.date;
        if (!bootReasonsByDate[date]) {
          bootReasonsByDate[date] = {
            totalBoots: 0,
            bootReasons: {},
            okBootReasons: {},
            problematicBootReasons: {}
          };
        }
        
        bootReasonsByDate[date].totalBoots += entry.count;
        bootReasonsByDate[date].bootReasons[entry.bootReason] = entry.count;
        
        // Classify boot reasons (assuming "Interval" is OK, others are problematic)
        if (entry.bootReason === 'Interval') {
          bootReasonsByDate[date].okBootReasons[entry.bootReason] = entry.count;
        } else {
          bootReasonsByDate[date].problematicBootReasons[entry.bootReason] = entry.count;
        }
      });
      
      // Process each date
      Object.keys(bootReasonsByDate).forEach(date => {
        const dayData = bootReasonsByDate[date];
        const problematicCount = Object.values(dayData.problematicBootReasons).reduce((sum, count) => sum + count, 0);
        const okCount = Object.values(dayData.okBootReasons).reduce((sum, count) => sum + count, 0);
        
        // Determine primary boot reason for display
        const primaryReason = Object.keys(dayData.bootReasons).reduce((a, b) => 
          dayData.bootReasons[a] > dayData.bootReasons[b] ? a : b
        );
        
        maps.bootReason[date] = {
          display: primaryReason,
          fullData: {
            date: date,
            totalBoots: dayData.totalBoots,
            problematicBoots: problematicCount,
            okBoots: okCount,
            bootReasons: dayData.bootReasons,
            okBootReasons: dayData.okBootReasons,
            problematicBootReasons: dayData.problematicBootReasons,
            summary: summary
          }
        };
      });
    }

    // For payload data, we'll estimate from connectivity data if available
    devicePerformanceData.device.connectivityStats?.forEach(day => {
      // Estimate payloads as a fraction of transmissions (this would be better from actual API)
      maps.payloads[day.date] = Math.floor((day.transmissionCount || 0) * 0.8);
    });

    // For RSSI data, use average for each day if available
    if (devicePerformanceData.device.rssiStats?.averageRSSI) {
      // Use the overall average for all dates (would be better with daily RSSI data)
      const avgRSSI = devicePerformanceData.device.rssiStats.averageRSSI;
      dateRange.forEach(date => {
        maps.rssi[date] = avgRSSI;
      });
    }
    
    return maps;
  };

  const dateRange = createDateRange();
  const dataMaps = createDataMaps(dateRange);

  // Boot reason hover handlers
  const handleBootReasonMouseEnter = (event, date, bootReasonData) => {
    if (!bootReasonData || !bootReasonData.fullData) return;
    
    // Get mouse position relative to viewport
    setBootReasonHoverPosition({
      x: event.clientX,
      y: event.clientY
    });
    
    setBootReasonHoverData({
      date: date,
      serialNumber: `Device ${devicePerformanceData?.device?.serialNumber || 'Unknown'}`,
      ...bootReasonData.fullData
    });
  };

  const handleBootReasonMouseLeave = () => {
    setBootReasonHoverData(null);
  };

  if (!devicePerformanceData?.device) {
    return (
      <WalteroCard sx={{ p: 3 }}>
        <WalteroTypography variant="h6">Device Performance Table</WalteroTypography>
        <Typography variant="body2" color="textSecondary">
          No performance data available.
        </Typography>
      </WalteroCard>
    );
  }

  return (
    <Box>
      {/* Performance Metrics Table */}
      <WalteroCard sx={{ p: 3, mb: 3 }}>
        <WalteroTypography variant="h6" gutterBottom>
          Daily Performance Metrics
        </WalteroTypography>
        
        <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Metric
                </TableCell>
                {dateRange.map(date => (
                  <TableCell key={date} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 120 }}>
                    {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Transmissions Row */}
              <TableRow hover>
                <TableCell sx={{ fontWeight: 'bold' }}>Transmissions</TableCell>
                {dateRange.map(date => (
                  <TableCell key={date}>
                    {dataMaps.transmissions[date] || 0}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Payloads Row */}
              <TableRow hover>
                <TableCell sx={{ fontWeight: 'bold' }}>Payloads</TableCell>
                {dateRange.map(date => (
                  <TableCell key={date}>
                    {dataMaps.payloads[date] || 0}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Average RSSI Row */}
              <TableRow hover>
                <TableCell sx={{ fontWeight: 'bold' }}>Avg RSSI (dBm)</TableCell>
                {dateRange.map(date => (
                  <TableCell key={date}>
                    {dataMaps.rssi[date] ? `${dataMaps.rssi[date]} dBm` : '-'}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Battery Voltage Row */}
              <TableRow hover>
                <TableCell sx={{ fontWeight: 'bold' }}>Battery Voltage (V)</TableCell>
                {dateRange.map(date => (
                  <TableCell key={date}>
                    {dataMaps.battery[date] ? `${dataMaps.battery[date]}V` : '-'}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Average Temperature Row */}
              <TableRow hover>
                <TableCell sx={{ fontWeight: 'bold' }}>Avg Temperature (°C)</TableCell>
                {dateRange.map(date => (
                  <TableCell key={date}>
                    {dataMaps.temperature[date] ? `${dataMaps.temperature[date]}°C` : '-'}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Boot Reason Row */}
              <TableRow hover>
                <TableCell sx={{ fontWeight: 'bold' }}>Boot Reason</TableCell>
                {dateRange.map(date => (
                  <TableCell key={date}>
                    {dataMaps.bootReason[date] ? (
                      <Chip
                        label={dataMaps.bootReason[date].display}
                        size="small"
                        color={dataMaps.bootReason[date].fullData?.problematicBoots > 0 ? 'error' : 'success'}
                        variant={dataMaps.bootReason[date].fullData?.problematicBoots > 0 ? 'filled' : 'outlined'}
                        onMouseEnter={(e) => handleBootReasonMouseEnter(e, date, dataMaps.bootReason[date])}
                        onMouseLeave={handleBootReasonMouseLeave}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </WalteroCard>

      {/* Hourly Connectivity Histogram */}
      {devicePerformanceData.device.connectivityPattern?.hourlyPattern && (
        <WalteroCard sx={{ p: 3 }}>
          <WalteroTypography variant="h6" sx={{ mb: 2 }}>
            Hourly Connection Pattern
          </WalteroTypography>
          
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Device transmission activity by hour of day over the selected date range.
          </Typography>

          <Box 
            ref={chartRef} 
            sx={{ 
              width: '100%', 
              height: '400px',
              minHeight: '400px'
            }} 
          />
        </WalteroCard>
      )}

      {/* Boot Reason Hover Overlay */}
      {bootReasonHoverData && (() => {
        const overlayWidth = 350;
        const overlayHeight = 280; // Estimated height
        
        // Center horizontally above the mouse cursor
        let left = bootReasonHoverPosition.x - (overlayWidth / 2);
        let top = bootReasonHoverPosition.y - overlayHeight - 10;
        
        // Adjust if overlay would go off screen
        if (left < 10) left = 10;
        if (left + overlayWidth > window.innerWidth - 10) {
          left = window.innerWidth - overlayWidth - 10;
        }
        if (top < 10) {
          // If no room above, show below cursor
          top = bootReasonHoverPosition.y + 20;
        }
        
        return (
          <div 
            style={{
              position: 'fixed',
              left: left,
              top: top,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '12px',
              zIndex: 1000,
              pointerEvents: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              width: `${overlayWidth}px`,
              maxHeight: '70vh',
              overflow: 'auto'
            }}
          >
        
          <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
            Boot Reason Analysis - {bootReasonHoverData.date}
          </div>
          <div style={{ marginBottom: '6px' }}>
            <strong>Device:</strong> {bootReasonHoverData.serialNumber}
          </div>
          <div style={{ marginBottom: '6px' }}>
            <strong>Total Boots:</strong> <span style={{ fontWeight: 'bold' }}>{bootReasonHoverData.totalBoots || 0}</span>
          </div>
          
          {/* Overall Device Summary */}
          {bootReasonHoverData.summary && (
            <div style={{ marginBottom: '8px', padding: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '4px' }}>Device Summary (All Time):</div>
              <div style={{ fontSize: '11px' }}>
                <span style={{ color: '#51CF66' }}>OK Boots: {bootReasonHoverData.summary.okBoots || 0}</span> | 
                <span style={{ color: '#FF6B6B', marginLeft: '8px' }}>Problematic: {bootReasonHoverData.summary.problematicBoots || 0}</span>
                {bootReasonHoverData.summary.problematicPercentage !== undefined && (
                  <span style={{ color: '#FFD93D', marginLeft: '8px' }}>
                    ({bootReasonHoverData.summary.problematicPercentage}% problematic)
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div style={{ marginBottom: '6px' }}>
            <strong>This Day - OK Boots:</strong> <span style={{ color: '#51CF66', fontWeight: 'bold' }}>{bootReasonHoverData.okBoots || 0}</span>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>This Day - Problematic Boots:</strong> <span style={{ 
              color: (bootReasonHoverData.problematicBoots || 0) > 0 ? '#FF6B6B' : '#51CF66', 
              fontWeight: 'bold' 
            }}>{bootReasonHoverData.problematicBoots || 0}</span>
          </div>
          
          {/* Show detailed boot reason breakdown */}
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Boot Reason Breakdown:</div>
            {Object.entries(bootReasonHoverData.bootReasons || {}).map(([reason, count]) => {
              const isProblematic = reason !== 'Interval';
              return (
                <div key={reason} style={{ 
                  marginLeft: '8px', 
                  fontSize: '11px', 
                  marginBottom: '2px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ 
                    color: isProblematic ? '#FF6B6B' : '#51CF66' 
                  }}>
                    {isProblematic ? '⚠️' : '✓'} {reason}:
                  </span>
                  <span style={{ 
                    fontWeight: 'bold', 
                    color: isProblematic ? '#FF6B6B' : '#51CF66',
                    marginLeft: '8px'
                  }}>
                    {count} boots
                  </span>
                </div>
              );
            })}
          </div>
          
          {/* Show percentage if multiple boot reasons */}
          {Object.keys(bootReasonHoverData.bootReasons || {}).length > 1 && (
            <div style={{ marginTop: '6px', fontSize: '11px', color: '#ccc' }}>
              {Object.entries(bootReasonHoverData.bootReasons || {}).map(([reason, count]) => {
                const percentage = ((count / bootReasonHoverData.totalBoots) * 100).toFixed(1);
                return `${reason}: ${percentage}%`;
              }).join(', ')}
            </div>
          )}
          </div>
        );
      })()}
    </Box>
  );
};

export default DevicePerformanceTable;